import { FC } from "react";
import { Casino } from "@mui/icons-material";
import { toBytes } from "viem";

import type { IServerSignature } from "@dzambalaorg/types-blockchain";
import {
  useAllowance,
  useMetamask,
  useServerSignature,
  useWriteContractWithSimulation,
} from "@dzambalaorg/react-hooks-eth";
import {
  convertDatabaseAssetToChainAsset,
  convertDatabaseAssetToTokenTypeAsset,
  getEthPrice,
} from "@framework/exchange";
import { ListAction, ListActionVariant } from "@framework/styled";
import { bool36ArrayToByte32 } from "@dzambalaorg/traits-v5";
import { AddressType, IContract, ILotteryRound } from "@framework/types";
import LotteryPurchaseABI from "@framework/abis/json/ExchangeLotteryFacet/purchaseLottery.json";
import { ZeroAddress } from "@framework/constants";

export interface ILotteryPurchaseButtonProps {
  className?: string;
  clearForm: () => void;
  disabled?: boolean;
  round: Partial<ILotteryRound>;
  ticketNumbers: Array<boolean>;
  variant?: ListActionVariant;
}

export const LotteryPurchaseButton: FC<ILotteryPurchaseButtonProps> = props => {
  const { clearForm, ticketNumbers, round, disabled, className, variant = ListActionVariant.button } = props;

  const { writeContractAsync } = useWriteContractWithSimulation();

  const referrer = ZeroAddress;

  const { execute: metaFnWithAllowance } = useAllowance(async (sign: IServerSignature, systemContract: IContract) => {
    const price = convertDatabaseAssetToChainAsset(round.price?.components);

    return writeContractAsync({
      address: systemContract.address as AddressType,
      abi: LotteryPurchaseABI,
      functionName: "purchaseLottery",
      args: [
        {
          externalId: round.id,
          expiresAt: sign.expiresAt,
          nonce: toBytes(sign.nonce, { size: 32 }),
          extra: bool36ArrayToByte32(ticketNumbers),
          receiver: round.contract?.address,
          referrer,
        },
        {
          tokenType: 2,
          token: round.ticketContract?.address,
          tokenId: "0",
          amount: "1",
        },
        price[0],
        sign.signature,
        {
          value: getEthPrice(round.price),
        },
      ],
    }).finally(clearForm);
  });

  const metaFnWithSign = useServerSignature((_values: null, sign: IServerSignature, systemContract: IContract) => {
    const price = convertDatabaseAssetToTokenTypeAsset(round.price?.components);

    return metaFnWithAllowance({ contract: systemContract.address, assets: price }, sign, systemContract);
  });

  const { execute: metaFn } = useMetamask(() => {
    return metaFnWithSign(
      {
        url: "/lottery/ticket/sign",
        method: "POST",
        data: {
          referrer,
          ticketNumbers: bool36ArrayToByte32(ticketNumbers),
          contractId: round.contractId,
        },
      },
      null,
    ) as Promise<void>;
  });

  const handlePurchase = () => {
    return metaFn();
  };

  return (
    <ListAction
      onClick={handlePurchase}
      icon={Casino}
      message="form.buttons.buy"
      buttonVariant="primary"
      className={className}
      dataTestId="LotteryPurchaseButton"
      disabled={
        disabled ||
        !round?.roundId ||
        // @ts-ignore
        (!!round.maxTickets && round.maxTickets > 0 && round.maxTickets <= round.ticketCount)
      }
      variant={variant}
    />
  );
};
